import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, fromEvent, Observable, Subject, takeUntil } from 'rxjs';
import { KycSubServiceType } from 'src/app/_modules/customers/_models/kyc-account-data';
import { AccountKycList } from 'src/app/models/accountKyc.model';
import { Area } from 'src/app/models/area.model';
import { ChecklistItem } from 'src/app/models/checklist-item.model';
import {
  AssetStatus,
  BuildingType,
  Customer,
  CustomerStats,
  FilterByOptions,
  InstallationAndPowerSource,
  ServiceType,
  SortByOptions,
  UserType,
} from 'src/app/models/customer.model';
import { ProjectDesignationDto } from 'src/app/models/projectDesignation.model';
import { Region } from 'src/app/models/region.model';
import { SystemComponents } from 'src/app/models/role.model';
import { ProjectType } from 'src/app/models/serviceConnection.model';
import { Tariff } from 'src/app/models/tariff.model';
import { UserForFilter } from 'src/app/models/user.model';
import { AreaService } from 'src/app/services/area.service';
import { AuthService } from 'src/app/services/auth.service';
import { CustomerService } from 'src/app/services/customer.service';
import { AccountKycService } from 'src/app/services/kyc/account-kyc.service';
import { ProjectDesignationService } from 'src/app/services/project-designation.service';
import { RegionService } from 'src/app/services/region.service';
import { TariffService } from 'src/app/services/tariff.service';
import { TranslationService } from 'src/app/services/translation.service';
import { UserService } from 'src/app/services/user.service';
import {
  GeographicalRegion,
  GeographicalRegionService,
  PermissionsEnum,
  PermissionsService,
  SubGeographicalRegion,
  SubGeographicalRegionService,
} from 'shared-lib';

@Component({
  selector: 'app-service-connection-charge-list',
  templateUrl: './service-connection-charge-list.component.html',
  styleUrls: ['./service-connection-charge-list.component.scss'],
})
export class ServiceConnectionChargeListComponent implements OnInit, OnDestroy{
  constructor(
    private customerService: CustomerService,
    private toastr: ToastrService,
    private regionService: RegionService,
    private areaService: AreaService,
    private projectDesignationService: ProjectDesignationService,
    private tariffService: TariffService,
    private translationService: TranslationService,
    private kycAccountService: AccountKycService,
    private userService: UserService, // private analytics: AngularFireAnalytics
    private router: Router,
    private route: ActivatedRoute,
    public permissionsService: PermissionsService,
    private authService: AuthService,
    private geographicalRegionService: GeographicalRegionService,
    private subGeographicalRegionService: SubGeographicalRegionService,
  ) {
    this.hasCimDataIntegration = this.authService.hasCimDataIntegration;
  }

  ngOnInit(): void {
    this.setFieldsVisibility();
    this.utilityId = parseInt(localStorage.getItem('utilityId') || '');
    this.currency = localStorage.getItem('currency') || '';
    this.subscribeToClickEvent();
    this.setFilters();
    if (this.hasCimDataIntegration) {
      this.getGeographicalRegions();
    } else {
      this.getRegions();
    }
    this.getTariffs();
    this.getProjectDesignations();
    this.getSltStatuses();
    this.setCheckedUsers();
    this.searchTextUpdate.pipe(debounceTime(500)).subscribe((_) => {
      if (this.searchText == '') this.search();
      this.searchFilterApplied = false;
    });
    this.selectedDateType = this.selectedDateType ?? this.dateTypes[0].name;
    this.selectedDateTypeDisplay =
      this.selectedDateTypeDisplay ?? this.dateTypes[0].translation;

    this.selectedSearchType =
      this.selectedSearchType ?? this.searchByOptions[0].name;
    this.selectedSearchTypeDisplay =
      this.selectedSearchTypeDisplay ?? this.searchByOptions[0].translation;

    document.addEventListener('scroll', this.onAnyScroll, true);
  }

  customers: Customer[] = [];
  accountKycs: AccountKycList[] = [];
  stats: CustomerStats = new CustomerStats();
  buName: string = this.translationService.getByKeyFromStorage('BU');
  utName: string = this.translationService.getByKeyFromStorage('UT');
  sltStatusName: string =
    this.translationService.getByKeyFromStorage('SltStatus');

  selectedSubserviceType?: number;
  showSubserviceTypes: boolean = false;
  subServiceTypeOptions: { value: number; displayValue: string }[] = [
    { value: KycSubServiceType.PublicLoad, displayValue: 'Public Load' },
    {
      value: KycSubServiceType.MeterReplacement,
      displayValue: 'Meter Replacement',
    },
    { value: KycSubServiceType.Migration, displayValue: 'Migration' },
    { value: KycSubServiceType.Separation, displayValue: 'Separation' },
    {
      value: KycSubServiceType.PointLoadTransformer,
      displayValue: 'Point Load Transformer',
    },
    {
      value: KycSubServiceType.DedicatedFeeder,
      displayValue: 'Dedicated Feeder',
    },
    { value: KycSubServiceType.NewService, displayValue: 'New Service' },
    { value: KycSubServiceType.UpgradeToKcg, displayValue: 'Upgrade to KCG' },
  ];

  mapSortBy: Record<number, any> = {
    [SortByOptions.NEWEST]: { label: 'COMMON.SORT_BY.NEWEST', arrow: 'no' },
    [SortByOptions.SLRN_ASC]: { label: 'COMMON.SLRN', arrow: 'up' },
    [SortByOptions.SLRN_DESC]: { label: 'COMMON.SLRN', arrow: 'down' },
    [SortByOptions.ACCNO_ASC]: {
      label: 'COMMON.SORT_BY.ACCOUNT_NO',
      arrow: 'up',
    },
    [SortByOptions.ACCNO_DESC]: {
      label: 'COMMON.SORT_BY.ACCOUNT_NO',
      arrow: 'down',
    },
    [SortByOptions.OLDEST]: { label: 'COMMON.SORT_BY.OLDEST', arrow: '' },
  };
  dateFrom: string;
  dateTo: string;

  searchText: string = '';
  searchTextUpdate = new Subject<string>();

  sortByValue: SortByOptions = SortByOptions.NEWEST;
  sortByOptions = SortByOptions;
  sortByLabelValue: any = this.mapSortBy[SortByOptions.NEWEST];
  filterByOptions = FilterByOptions;
  filterByValue?: FilterByOptions;
  assetStatusOptions = AssetStatus;
  public AssetStatus = AssetStatus;
  withAccNoSelected = 0;
  count: number = 0;
  pageSize: number = 10;
  currentPage: number = 1;

  showUpload: boolean = false;
  showSortBy: boolean = false;
  ddPos = { top: '0px', left: '0px' };

  historyItems: any[] = [];
  isViewHistory: boolean = true;

  clickObservable: Observable<Event> = fromEvent(document, 'click');
  private ngUnsubscribe = new Subject<void>();

  usersFilter: any; // Stores value of filter
  mobileUsers: UserForFilter[] = []; // Store only mobile users
  adminUsers: UserForFilter[] = []; // Store only admin users
  allUsers: UserForFilter[] = []; // Store mobile or admin users depending on filter
  filteredUsers: UserForFilter[] = []; // Used for display and for filtering users list
  selectedUsers: number[] = [];

  regions: Region[] = [];
  areas: Area[] = [];
  geographicalRegions: (GeographicalRegion & { checked?: boolean })[] = [];
  allSubGeographicalRegions: (SubGeographicalRegion & { checked?: boolean })[] = [];
  subGeographicalRegions: (SubGeographicalRegion & { checked?: boolean })[] = [];
  selectedSubGeographicalRegions: number[] = [];
  hasCimDataIntegration: boolean = false;
  projectDesignations: ProjectDesignationDto[] = [];
  selectedProjectDesignationIds: number[] = [];
  allAreas: Area[] = [];
  tariff: number = 0;
  tariffs: Tariff[] = [];
  selectedAreas: number[] = [];
  selectedRegions: number[] = [];
  serviceType?: number;

  userType?: number;

  mobileUserType: UserType[] = [
    UserType.FirstCapturedUser,
    UserType.LastCapturedUser,
  ];
  adminUserType: UserType[] = [
    UserType.ValidatedBy,
    UserType.ReassignedBy,
    UserType.AuditedBy,
  ];

  kycTag?: number;
  kycTagOptions = [
    { name: 'Existing', value: 1, translation: 'COMMON.TAG.EXISTING' },
    { name: 'New Customer', value: 2, translation: 'COMMON.TAG.NEW_CUSTOMER' },
  ];

  kycStatus?: number;
  kycStatusOptions = [
    {
      name: 'Pending',
      value: 1,
      translation: 'COMMON.KYC_STATUS_OPTIONS.PENDING',
    },
    {
      name: 'Rejected',
      value: 2,
      translation: 'COMMON.KYC_STATUS_OPTIONS.REJECTED',
    },
    {
      name: 'Approved',
      value: 4,
      translation: 'COMMON.KYC_STATUS_OPTIONS.APPROVED',
    },
  ];
  sltStatus?: number;
  // sltStatusOptions = [
  //   { name: this.translationService.getByKeyFromStorage('NSLT'), value: 0 },
  //   { name: this.translationService.getByKeyFromStorage('SLT'), value: 1 },
  // ];

  useOfPremises?: number;
  uopOptions = [
    { name: 'Residential', value: BuildingType.Residential },
    { name: 'Commercial', value: BuildingType.Commercial },
    { name: 'Industrial', value: BuildingType.Industrial },
    { name: 'Mixed', value: BuildingType.Mixed },
    { name: 'Religious Center', value: BuildingType.ReligiousCenter },
  ];

  clicked: boolean = false;

  selectedFilters: ChecklistItem[] = [];
  utilityId: number = 0;
  allCheckboxesChecked: boolean = false;
  dateTypes = [
    {
      name: 'LastUpdateTime',
      value: 'Last Update Time',
      translation: 'COMMON.DATE_TYPE.LAST_UPDATE',
    },
    {
      name: 'TaggedDate',
      value: 'Tagged Date',
      translation: 'COMMON.DATE_TYPE.TAGGED',
    },
    {
      name: 'ValidatedDate',
      value: 'Validated Date',
      translation: 'COMMON.DATE_TYPE.VALIDATED',
    },
  ];
  searchByOptions = [
    {
      name: 12,
      value: 'Application No.',
      translation: 'COMMON.TABLE_HEADERS.APP_NUM',
    },
    {
      name: 1,
      value: 'Account No.',
      translation: 'CUSTOMERS.SEARCH_BY.ACCOUNT_NO',
    },
    { name: 11, value: 'SPN', translation: 'COMMON.CONTRACT_NUMBER' },
  ];
  selectedDateType: string;
  selectedDateTypeDisplay: string;
  showSelectDateType: boolean = false;
  selectedSearchType: number;
  selectedSearchTypeDisplay: string;
  showSelectSearchType: boolean = false;
  searchFilterApplied: boolean = false;
  regularizedInfoOpen: boolean = false;
  totalInfoOpen: boolean = false;
  sltOptions: {
    id: number;
    displayValue: string;
    installationAndPowerSource: number;
  }[] = [];
  isSltDropdownDisabled: boolean = false;
  isServiceTypeFieldVisible: boolean = true;
  isRegularizationUploadVisible: boolean = true;

  currency: string = '';
  selectedProjectTypes: number[] = [];
  projectTypeOptions: {
    value: number;
    name: string;
    checked: boolean;
  }[] = [
    { value: ProjectType.MAP, name: 'MAP', checked: false },
    { value: ProjectType.NMAP, name: 'NMAP', checked: false },
    { value: ProjectType.KCG, name: 'KCG', checked: false },
  ];

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.searchTextUpdate.complete();
    document.removeEventListener('scroll', this.onAnyScroll, true);
  }

  private onAnyScroll = (): void => {
    this.showSelectSearchType = false;
    this.showSelectDateType = false;
    this.showSortBy = false;
    this.showUpload = false;
  };

  private getData(): void {
    this.reloadTable();
  }

  selectServiceConnection(id: number) {
    this.router.navigate(['service-connection-details', id], {
      relativeTo: this.route,
    });
    this.setFilterStorage();
  }

  getProjectDesignations() {
    this.projectDesignationService.getAllForSelect(this.utilityId).subscribe({
      next: (response: any) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.projectDesignations = responseData.data;
          if (this.selectedProjectDesignationIds.length > 0) {
            (this.projectDesignations as any[]).forEach((pd) => {
              pd.checked = this.selectedProjectDesignationIds.includes(pd.id);
            });
            this.createFilterCards();
          }
        }
      },

      error: (_) => this.toastr.error('Error while fetching project designations'),
    });
  }

  setFilters() {
    var filter = localStorage.getItem('filterServiceRequestAuthorization');
    if (!filter) return;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'ServiceRequestAuthorization') return;
    this.currentPage = filterObject.filter.pageInfo.page;
    this.pageSize = filterObject.filter.pageInfo.pageSize;
    this.count = filterObject.filter.pageInfo.count;
    this.sortByValue = filterObject.filter.filterParams.sortBy;
    this.searchText = filterObject.filter.filterParams.search;
    this.filterByValue = filterObject.filter.filterParams.filterBy;
    this.dateFrom = filterObject.filter.filterParams.dateFrom;
    this.dateTo = filterObject.filter.filterParams.dateTo;
    this.selectedDateType = filterObject.filter.filterParams.dateType;
    this.selectedSearchType = filterObject.filter.filterParams.searchType;
    this.selectedSubserviceType =
      filterObject.filter.filterParams.selectedSubserviceType;
    this.selectedDateTypeDisplay =
      this.dateTypes.find(
        (type) => type.name == filterObject.filter.filterParams.dateType,
      )?.translation ?? this.dateTypes[0].translation;
    this.selectedSearchTypeDisplay =
      this.searchByOptions.find(
        (type) => type.name == filterObject.filter.filterParams.searchType,
      )?.translation ?? this.searchByOptions[0].translation;
    this.tariff = filterObject.filter.filterParams.tariffId
      ? filterObject.filter.filterParams.tariffId
      : 0;
    // this.serviceType = filterObject.filter.filterParams.serviceType
    //   ? filterObject.filter.filterParams.serviceType
    //   : undefined;
    this.userType = filterObject.filter.filterParams.userType
      ? filterObject.filter.filterParams.userType
      : undefined;
    this.kycStatus = filterObject.filter.filterParams.kycStatus;
    this.kycTag = filterObject.filter.filterParams.kycTag;
    this.sltStatus = filterObject.filter.filterParams.sltStatus
      ? filterObject.filter.filterParams.sltStatus
      : undefined;
    this.useOfPremises = filterObject.filter.filterParams.useOfPremises
      ? filterObject.filter.filterParams.useOfPremises
      : undefined;
    this.selectedAreas = filterObject.filter.filterParams.selectedAreas
      ? filterObject.filter.filterParams.selectedAreas
      : [];
    this.selectedRegions = filterObject.filter.filterParams.selectedRegions
      ? filterObject.filter.filterParams.selectedRegions
      : [];
    this.selectedSubGeographicalRegions = filterObject.filter.filterParams
      .selectedSubGeographicalRegions
      ? filterObject.filter.filterParams.selectedSubGeographicalRegions
      : [];
    this.selectedUsers = filterObject.filter.filterParams.selectedUsers
      ? filterObject.filter.filterParams.selectedUsers
      : [];

    this.sortByLabelValue = this.mapSortBy[this.sortByValue];

    if (this.filterByValue == FilterByOptions.ONBOARDED) {
      this.kycStatus = filterObject.filter.filterParams.kycStatus;
      this.kycTag = filterObject.filter.filterParams.kycTag;
    }

    this.selectedProjectDesignationIds =
          filterObject.filter.filterParams.selectedProjectDesignationIds ?? [];
    if (this.selectedProjectDesignationIds && this.selectedProjectDesignationIds.length > 0) {
      (this.projectDesignations as any[]).forEach((pd) => {
        if (this.selectedProjectDesignationIds.includes(pd.id)) {
          pd.checked = true;
        } else {
          pd.checked = false;
        }
      });
    }
    this.selectedProjectTypes = filterObject.filter.filterParams
          .selectedProjectTypes
          ? filterObject.filter.filterParams.selectedProjectTypes
          : [];
    if (this.selectedProjectTypes && this.selectedProjectTypes.length > 0) {
      this.projectTypeOptions.forEach((pt) => {
        if (this.selectedProjectTypes.includes(pt.value)) {
          pt.checked = true;
        } else {
          pt.checked = false;
        }
      });
    }
  }

  setCheckedAreas() {
    var filter = localStorage.getItem('filterServiceRequestAuthorization');
    if (!filter) return;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'ServiceRequestAuthorization') return;
    if (filterObject.filter.filterParams.selectedAreas) {
      this.selectedAreas = filterObject.filter.filterParams.selectedAreas;
      this.areas = this.allAreas.filter((area) =>
        this.regions.some(
          (region) => region.id == area.regionId && region.checked,
        ),
      );
      this.areas.forEach((element) => {
        if (this.selectedAreas.includes(element.id)) element.checked = true;
        else element.checked = false;
      });
    }
  }

  async setCheckedUsers() {
    var filter = localStorage.getItem('filterServiceRequestAuthorization');
    if (!filter) return;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'ServiceRequestAuthorization') return;
    if (
      //   filterObject.filter.filterParams.selectedUsers &&
      filterObject.filter.filterParams.userType
    ) {
      this.userType = filterObject.filter.filterParams.userType;
      this.getUsersForFilter(true, filterObject);
    }
  }

  setCheckedRegions() {
    var filter = localStorage.getItem('filterServiceRequestAuthorization');
    if (!filter) return;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'ServiceRequestAuthorization') return;
    if (filterObject.filter.filterParams.selectedRegions) {
      this.selectedRegions = filterObject.filter.filterParams.selectedRegions;
      this.regions.forEach((element) => {
        if (this.selectedRegions.includes(element.id)) element.checked = true;
        else element.checked = false;
      });
    }
  }

  setCheckedGeographicalRegions() {
    var filter = localStorage.getItem('filterServiceRequestAuthorization');
    if (!filter) return;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'ServiceRequestAuthorization') return;
    if (filterObject.filter.filterParams.selectedGeographicalRegions) {
      const selectedIds =
        filterObject.filter.filterParams.selectedGeographicalRegions;
      this.geographicalRegions.forEach((element) => {
        if (selectedIds.includes(element.id)) element.checked = true;
        else element.checked = false;
      });
    }
  }

  setCheckedSubGeographicalRegions() {
    var filter = localStorage.getItem('filterServiceRequestAuthorization');
    if (!filter) return;
    var filterObject = JSON.parse(filter);
    if (filterObject.type != 'ServiceRequestAuthorization') return;
    if (filterObject.filter.filterParams.selectedSubGeographicalRegions) {
      this.selectedSubGeographicalRegions =
        filterObject.filter.filterParams.selectedSubGeographicalRegions;
      this.subGeographicalRegions.forEach((element) => {
        if (this.selectedSubGeographicalRegions.includes(element.id as number))
          element.checked = true;
        else element.checked = false;
      });
    }
  }

  reloadTable(page: any = null) {
    this.allCheckboxesChecked = false;
    if (page) this.currentPage = page;
    let obj = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
        count: this.count,
      },
      filterParams: this.getKycFilterObject(),
      utilityId: this.utilityId,
    };

    this.getAll(obj);
  }

  getKycFilterObject() {
    let obj = {
      sortBy: this.sortByValue,
      search: this.searchText,
      dateFrom: this.dateFrom ? this.dateFrom : null,
      dateTo: this.dateTo ? `${this.dateTo}T23:59:59` : null,
      kycStatus: this.kycStatus,
      kycTag: this.kycTag,
      selectedAreas: this.selectedAreas.length > 0 ? this.selectedAreas : null,
      selectedRegions:
        this.selectedRegions.length > 0 ? this.selectedRegions : null,
      selectedSubGeographicalIds:
        this.selectedSubGeographicalRegions.length > 0
          ? this.selectedSubGeographicalRegions
          : null,
      selectedIds: this.customers
        .filter((x) => x.isChecked == true)
        .map((x) => x.id),
      selectedSltStatus: this.sltStatus ? Number(this.sltStatus) : null,
      selectedSubserviceType: this.selectedSubserviceType
        ? Number(this.selectedSubserviceType)
        : null,
        searchType: this.selectedSearchType,
        selectedProjectTypes:
            this.selectedProjectTypes.length > 0 ? this.selectedProjectTypes : null,
     
      selectedProjectDesignations: this.selectedProjectDesignationIds
    };
    return obj;
  }

  download() {
    this.kycAccountService.downloadServiceConnection(this.getKycFilterObject()).subscribe({
      next: (response: any) => {
        const data = response.body;
        if (data?.status === 200 || data?.status === 'OK') {
          this.toastr.success('File downloaded');
          window.location.href = data.data;
        } else {
          this.toastr.error(data?.message);
        }
      },
      error: (_: any) => this.toastr.error('Error occured'),
    });
  }

  setFilterStorage() {
    let obj = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
        count: this.count,
      },
      filterParams: this.getFilterObjectForStorage(),
      utilityId: this.utilityId,
    };

    localStorage.setItem('cameFrom', 'ServiceRequestAuthorization');
    localStorage.setItem(
      'filterServiceRequestAuthorization',
      JSON.stringify({ type: 'ServiceRequestAuthorization', filter: obj }),
    );
  }

  selectSubserviceType(e: any) {
    if (e.target.value != '') {
      this.selectedSubserviceType = e.target.value;
    } else {
      this.selectedSubserviceType = undefined;
    }
    this.createFilterCards();
  }

  selectProjectType() {
    this.createFilterCards();
  }

  private getAll(obj: any) {
    this.kycAccountService.getAllServiceConnection(obj).subscribe({
      next: (response: any) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.accountKycs = responseData.data.data;
          this.accountKycs.forEach(
            (x) =>
              (x.subserviceType =
                x.subserviceType?.replace(/([a-z])([A-Z])/g, '$1 $2') ?? ''),
          );
          this.count = responseData.data.count ?? this.count;
          if (responseData.message != '') {
            this.toastr.warning(responseData.message);
          }
        } else if (response?.status == 204) {
          this.toastr.warning('No content');
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => this.toastr.error('Error occured'),
    });
  }

  checkCustomer(id: number) {
    const customer = this.customers.find((x) => x.id === id);
    if (customer) {
      customer.isChecked = !customer.isChecked;
      this.areAllCheckboxesChecked();
    }
  }

  areAllCheckboxesChecked() {
    this.allCheckboxesChecked = this.customers.every((x) => x.isChecked);
  }

  checkAllCheckboxes() {
    this.customers.forEach((x) => (x.isChecked = !this.allCheckboxesChecked));
    this.allCheckboxesChecked = !this.allCheckboxesChecked;
  }

  pageChange(value: any) {
    this.currentPage = value;
    this.reloadTable();
  }

  sortBy(option: SortByOptions) {
    this.sortByValue = option;
    this.sortByLabelValue = this.mapSortBy[option];
    this.reloadTable();
  }

  search() {
    this.searchFilterApplied = this.searchText ? true : false;
    this.createFilterCards();
  }

  onFilterBy(option: FilterByOptions) {
    if (this.filterByValue == option) {
      this.filterByValue = undefined;
    } else {
      this.filterByValue = option;
    }
    this.regularizedInfoOpen = false;
    this.totalInfoOpen = false;
    this.createFilterCards();
    this.reloadTable(1);
  }

  onFilterByNew(option: FilterByOptions, withAccNo: number) {
    this.withAccNoSelected = withAccNo;
    this.onFilterBy(option);
  }

  getFilterObjectForStorage() {
    let obj = {
      sortBy: this.sortByValue,
      search: this.searchText,
      filterBy: this.filterByValue,
      dateFrom: this.dateFrom ? this.dateFrom : null,
      dateTo: this.dateTo ? this.dateTo : null,
      dateType: this.selectedDateType,
      searchType: this.selectedSearchType,
      tariffId: this.tariff != 0 ? this.tariff : null,
      serviceType: this.serviceType ? this.serviceType : null,
      userType: this.userType ? this.userType : null,
      kycStatus: this.kycStatus,
      kycTag: this.kycTag,
      sltStatus: this.sltStatus ? this.sltStatus : null,
      useOfPremises: this.useOfPremises ? this.useOfPremises : null,
      selectedAreas: this.selectedAreas.length > 0 ? this.selectedAreas : null,
      selectedUsers: this.selectedUsers.length > 0 ? this.selectedUsers : null,
      selectedRegions:
        this.selectedRegions.length > 0 ? this.selectedRegions : null,
      selectedGeographicalRegions:
        this.geographicalRegions.filter((x) => x.checked).map((x) => x.id).length > 0
          ? this.geographicalRegions.filter((x) => x.checked).map((x) => x.id)
          : null,
      selectedSubGeographicalRegions:
        this.selectedSubGeographicalRegions.length > 0
          ? this.selectedSubGeographicalRegions
          : null,
      selectedIds: this.customers
        .filter((x) => x.isChecked == true)
        .map((x) => x.id),
      selectedSubserviceType: this.selectedSubserviceType
        ? this.selectedSubserviceType
        : null,
      selectedProjectTypes: this.selectedProjectTypes
        ? this.selectedProjectTypes
        : null,
      selectedProjectDesignationIds:
        this.selectedProjectDesignationIds.length > 0
          ? this.selectedProjectDesignationIds
          : null,
    };
    return obj;
  }

  viewHistory(id: number) {
    this.customerService.getHistory(id).subscribe({
      next: (response: any) => {
        this.historyItems = response?.body?.data ?? [];
      },
    });
  }



  private subscribeToClickEvent() {
    this.clickObservable
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((e: any) => {
        this.showUpload = false;
        this.showSortBy = false;
        this.showSelectDateType = false;
        this.showSelectSearchType = false;
      });
  }

  datesValid() {
    if (this.dateFrom && this.dateTo && this.dateTo?.toString() != '') {
      const valid: boolean =
        new Date(this.dateFrom).getTime() <
        new Date(`${this.dateTo.toString()}T23:59:59`).getTime();
      return valid;
    }

    return true;
  }

  private getRegions() {
    this.regionService.getAllAssignedForSelect().subscribe({
      next: (response: any) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.regions = responseData.data;
          this.setCheckedRegions();
          this.getAreas();
        } else if (response?.status == 204) {
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  private getAreas() {
    this.areaService.getAllAssignedForSelect().subscribe({
      next: (response: any) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.allAreas = responseData.data;
          this.areas = this.allAreas.filter((area) =>
            this.regions.some(
              (region) => region.id == area.regionId && region.checked,
            ),
          );
          this.setCheckedAreas();
          this.createFilterCards();
          this.removeFilterStorage();
        } else if (response?.status == 204) {
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  private getGeographicalRegions() {
    this.geographicalRegionService.getAllForSelect().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.geographicalRegions = responseData.data;
          this.setCheckedGeographicalRegions();
          this.getSubGeographicalRegions();
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  private getSubGeographicalRegions() {
    this.subGeographicalRegionService.getAllForMappings().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.allSubGeographicalRegions = responseData.data;
          this.subGeographicalRegions = this.allSubGeographicalRegions.filter(
            (sub) =>
              this.geographicalRegions.some(
                (region) =>
                  region.id == sub.geographicalRegion.id && region.checked,
              ),
          );
          this.setCheckedSubGeographicalRegions();
          this.createFilterCards();
          this.removeFilterStorage();
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  getTariffs() {
    this.tariffService.getAllForSelect().subscribe({
      next: (response: any) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.tariffs = responseData.data;
          this.createFilterCards();
          this.removeFilterStorage();
        } else if (response?.status == 204) {
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  private getRoleCapacityRestriction(): InstallationAndPowerSource | null {
    const rolePermissions = localStorage.getItem('permissions');
    const permissions = rolePermissions ? JSON.parse(rolePermissions) : [];
    const hasHigh = permissions.includes(PermissionsEnum.ServiceRequestAuth_HighCapacity);
    const hasStandard = permissions.includes(PermissionsEnum.ServiceRequestAuth_StandardCapacity);
    if (hasHigh && !hasStandard) return InstallationAndPowerSource.High_Capacity;
    if (!hasHigh && hasStandard) return InstallationAndPowerSource.Standard;
    return null;
  }

  getSltStatusFromRolePermission() {
    const restriction = this.getRoleCapacityRestriction();
    if (restriction == null) return this.sltStatus;
    return this.sltOptions.find(s => s.installationAndPowerSource == restriction)?.id;
  }

  filterSltOptionsByRolePermission(options: typeof this.sltOptions) {
    const restriction = this.getRoleCapacityRestriction();
    if (restriction == null) return options;
    return options.filter(s => s.installationAndPowerSource == restriction);
  }

  getSltStatuses() {
    this.customerService.getAllSltStatuses().subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.sltOptions = responseData.data.map((item: any): any => ({
            id: item.id,
            displayValue: item.value,
            installationAndPowerSource:
              InstallationAndPowerSource[
                item.installationAndPowerSource as keyof typeof InstallationAndPowerSource
              ],
          }));
          this.sltOptions = this.filterSltOptionsByRolePermission(this.sltOptions);
          this.sltStatus = this.getSltStatusFromRolePermission();
          this.isSltDropdownDisabled = this.getRoleCapacityRestriction() != null;
          this.createFilterCards();
          this.removeFilterStorage();
          this.getData();
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  selectTariff(e: any) {
    this.tariff = +e.target.value;
    this.createFilterCards();
    this.reloadTable(1);
  }

  selectServiceType(e: any) {
    if (e.target.value != '') {
      this.serviceType = e.target.value;
    } else {
      this.serviceType = undefined;
    }
    this.createFilterCards();
    this.reloadTable(1);
  }

  resetUserList() {
    this.allUsers.forEach((user) => {
      user.checked = false;
    });
    this.allUsers = this.filteredUsers = [...[]];
  }

  selectUserType(e: any) {
    if (this.userType) this.resetUserList();

    if (e.target.value != '') {
      this.userType = e.target.value;
      this.getUsersForFilter(false);
    } else {
      this.userType = undefined;
    }

    this.createFilterCards();
  }

  getUsersForFilter(isFromCache: boolean, filterObject?: any) {
    if (
      this.userType == UserType.FirstCapturedUser ||
      this.userType == UserType.LastCapturedUser
    ) {
      // Dobavi mobilne usere iz baze ako nisi
      if (this.mobileUsers.length === 0) {
        this.userService
          .getUsersForFilter(SystemComponents.Mobile)
          .subscribe((resp: any) => {
            this.mobileUsers = resp.data;
            this.allUsers = this.filteredUsers = this.mobileUsers;
            if (isFromCache) {
              this.selectedUsers =
                filterObject.filter.filterParams.selectedUsers;
              this.allUsers.forEach((element) => {
                if (this.selectedUsers.includes(element.id))
                  element.checked = true;
                else element.checked = false;
              });
            }
            this.createFilterCards();
          });
      } else {
        this.allUsers = this.filteredUsers = [...this.mobileUsers];
      }
    } else {
      if (this.adminUsers.length === 0) {
        this.userService
          .getUsersForFilter(SystemComponents.Admin)
          .subscribe((resp: any) => {
            this.adminUsers = resp.data;
            this.allUsers = this.filteredUsers = this.adminUsers;
            if (isFromCache) {
              this.selectedUsers =
                filterObject.filter.filterParams.selectedUsers;
              this.allUsers.forEach((element) => {
                if (this.selectedUsers.includes(element.id))
                  element.checked = true;
                else element.checked = false;
              });
              this.createFilterCards();
            }
          });
      } else {
        this.allUsers = this.filteredUsers = [...this.adminUsers];
      }
    }
  }

  applySearchFilter(e: any) {
    this.filteredUsers = this.allUsers.filter((user) => user.name.includes(e));
  }

  selectUsers(e: any) {
    if (e.status) {
      this.allUsers.push(e.value);
    } else {
      var index = this.allUsers.indexOf(e.value);
      this.allUsers.splice(index, 1);
    }
    this.createFilterCards();
  }

  selectKycTag(e: any) {
    if (e.target.value != '') {
      this.kycTag = +e.target.value;
    } else {
      this.kycTag = undefined;
    }
    this.createFilterCards();
  }

  selectKycStatus(e: any) {
    if (e.target.value != '') {
      this.kycStatus = +e.target.value;
    } else {
      this.kycStatus = undefined;
    }
    this.createFilterCards();
  }

  selectSltStatus(e: any) {
    if (e.target.value != '') {
      this.sltStatus = e.target.value;
      const isHighCapacity =
        this.sltOptions.find((s) => s.id == this.sltStatus)
          ?.installationAndPowerSource ==
        InstallationAndPowerSource.High_Capacity;
      if (isHighCapacity) {
        this.showSubserviceTypes = true;
      } else {
        this.showSubserviceTypes = false;
        this.selectedSubserviceType = undefined;
      }
    } else {
      this.sltStatus = undefined;
    }
    this.createFilterCards();
  }

  selectUseOfPremises(e: any) {
    if (e.target.value != '') {
      this.useOfPremises = e.target.value;
    } else {
      this.useOfPremises = undefined;
    }
    this.createFilterCards();
    this.reloadTable(1);
  }

  selectProjectDesignation(e: any) {
    this.createFilterCards();
  }

  selectRegion(e: any) {
    //list of areas for selected regions
    this.areas = this.allAreas.filter((area) =>
      this.regions.some(
        (region) => region.id == area.regionId && region.checked,
      ),
    );
    this.allAreas.forEach((area) => {
      if (!this.areas.some((a) => a.id === area.id)) {
        area.checked = false; //uncheck areas for unchecked regions
      }
    });
    this.createFilterCards();
  }

  selectArea(e: any) {
    this.createFilterCards();
  }

  selectGeographicalRegion(e: any) {
    this.subGeographicalRegions = this.allSubGeographicalRegions.filter((sub) =>
      this.geographicalRegions.some(
        (region) => region.id == sub.geographicalRegion.id && region.checked,
      ),
    );
    this.allSubGeographicalRegions.forEach((sub) => {
      if (!this.subGeographicalRegions.some((s) => s.id === sub.id)) {
        sub.checked = false;
      }
    });
    this.createFilterCards();
  }

  applyRegionFilter() {
    this.reloadTable(1);
  }

  applyAreaFilter() {
    this.reloadTable(1);
  }

  applyUserFilter() {
    this.reloadTable(1);
  }

  removeFilter(item: ChecklistItem) {
    if (item.property == this.buName) {
      this.regions.forEach((region) => {
        if (region.name === item.selectedValue) {
          region.checked = false;
        }
      });
      this.selectRegion(event);
      this.clicked = !this.clicked;
    } else if (item.property == this.utName) {
      this.areas.forEach((area) => {
        if (area.name === item.selectedValue) {
          area.checked = false;
        }
      });
      this.clicked = !this.clicked;
    } else if (item.property == 'User') {
      this.allUsers.forEach((user) => {
        if (user.name === item.selectedValue) {
          user.checked = false;
        }
      });
      this.clicked = !this.clicked;
    } else if (item.property == 'Tariff') {
      this.tariff = 0;
    } else if (item.property == 'Service type') {
      this.serviceType = undefined;
    } else if (item.property == 'User type') {
      this.userType = undefined;
      this.allUsers.forEach((user) => {
        // Clear list if he selects same filter again to get all cleared
        user.checked = false;
      });
      this.allUsers = this.filteredUsers = [...[]];
    } else if (item.property == 'Slt status') {
      this.sltStatus = undefined;
    } else if (item.property == 'Use of premises') {
      this.useOfPremises = undefined;
    } else if (item.property == 'Type') {
      this.filterByValue = this.filterByOptions.TOTAL;
    } else if (item.property == 'Status') {
      this.kycStatus = undefined;
    } else if (item.property == 'Tag') {
      this.kycTag = undefined;
    } else if (item.property == 'Service Request Type') {
      this.selectedSubserviceType = undefined;
    } else if (item.property == 'Search') {
      this.searchText = '';
    } else if (item.property == 'Date From') {
      this.dateFrom = '';
    } else if (item.property == 'Date To') {
      this.dateTo = '';
    }   else if (item.property == 'Project Status') {
        this.projectTypeOptions.forEach((pt) => {
            if (pt.name === item.selectedValue) {
                pt.checked = false;
            }});
            } else if (item.property == 'Project Designation') {
      (this.projectDesignations as any[]).forEach((pd) => {
        if (pd.name === item.selectedValue) {
          pd.checked = false;
        }
      });
      this.clicked = !this.clicked;
    } else if (item.property == 'Geographical Region') {
      this.geographicalRegions.forEach((region) => {
        if (region.name === item.selectedValue) {
          region.checked = false;
        }
      });
      this.selectGeographicalRegion(item);
      this.clicked = !this.clicked;
    } else if (item.property == 'Sub Geographical Region') {
      this.subGeographicalRegions.forEach((sub) => {
        if (sub.name === item.selectedValue) {
          sub.checked = false;
        }
      });
      this.clicked = !this.clicked;
    }

    this.createFilterCards();

    if (this.selectedFilters.length == 0) {
      this.regions.forEach((region) => {
        region.checked = false;
      });
      this.reloadTable(1);
    }
  
}

  resetFilter() {
    this.selectedFilters = [];

    this.regions.forEach((region) => {
      region.checked = false;
    });

    this.areas.forEach((area) => {
      area.checked = false;
    });
    this.areas = [];

    this.geographicalRegions.forEach((region) => (region.checked = false));
    this.allSubGeographicalRegions.forEach((sub) => (sub.checked = false));
    this.subGeographicalRegions = [];

    this.allUsers.forEach((user) => {
      user.checked = false;
    });
    this.allUsers = this.filteredUsers = [...[]];
    this.selectedAreas = [];
    this.selectedRegions = [];
    this.selectedSubGeographicalRegions = [];
    this.tariff = 0;
    this.serviceType = undefined;
    this.userType = undefined;
    this.sltStatus = undefined;
    this.useOfPremises = undefined;
    this.filterByValue = this.filterByOptions.TOTAL;
    this.selectedSubserviceType = undefined;

    this.searchText = '';
    this.dateFrom = '';
    this.dateTo = '';

    this.clicked = !this.clicked;
    this.kycStatus = undefined;
    this.kycTag = undefined;
    
    this.selectedProjectDesignationIds = [];
    (this.projectDesignations as any[]).forEach((pd) => (pd.checked = false));
        this.projectTypeOptions.forEach((pt) => {
            pt.checked = false;
        });
        this.selectedProjectTypes = [];
    this.reloadTable(1);
  }

  createFilterCards() {
    this.selectedFilters = [];

    let selectedAreas = this.areas.filter((x) => x.checked);
    let selectedUsers = this.allUsers.filter((x) => x.checked);
    let selectedGeoRegions = this.geographicalRegions.filter((x) => x.checked);
    let selectedSubGeoRegions = this.subGeographicalRegions.filter(
      (x) => x.checked,
    );

    this.selectedAreas = this.areas.filter((x) => x.checked).map((x) => x.id);
    this.selectedSubGeographicalRegions = selectedSubGeoRegions.map(
      (x) => x.id as number,
    );
    this.selectedUsers = this.allUsers
      .filter((x) => x.checked)
      .map((x) => x.id);
    this.selectedRegions = this.regions
      .filter((x) => x.checked)
      .map((x) => x.id);
    
    if (this.projectDesignations.length > 0) {
      this.selectedProjectDesignationIds = (this.projectDesignations as any[])
        .filter((x) => x.checked)
        .map((x) => x.id);
    }

    //geographical regions
    for (var i = 0; i < selectedGeoRegions.length; i++) {
      this.selectedFilters.push(
        new ChecklistItem(
          'Geographical Region',
          selectedGeoRegions[i].name,
          'COMMON.GEOGRAPHICAL_REGION.SINGLE',
        ),
      );
    }

    //areas
    for (var i = 0; i < selectedAreas.length; i++) {
      this.selectedFilters.push(
        new ChecklistItem(
          this.utName,
          selectedAreas[i].name,
          'COMMON.UT.SINGLE',
        ),
      );
    }

    //sub geographical regions
    for (var i = 0; i < selectedSubGeoRegions.length; i++) {
      this.selectedFilters.push(
        new ChecklistItem(
          'Sub Geographical Region',
          selectedSubGeoRegions[i].name,
          'COMMON.SUB_GEOGRAPHICAL_REGION.SINGLE',
        ),
      );
    }

    //users
    for (var i = 0; i < selectedUsers.length; i++) {
      this.selectedFilters.push(
        new ChecklistItem('User', selectedUsers[i].name),
      );
    }

    //tariff
    if (this.tariff != 0) {
      let tariffName = this.tariffs.filter((x) => x.id == this.tariff)[0]?.name;
      this.selectedFilters.push(new ChecklistItem('Tariff', tariffName));
    }

    //service type
    if (this.serviceType) {
      var serviceTypeName = ServiceType[this.serviceType!];
      this.selectedFilters.push(
        new ChecklistItem('Service type', serviceTypeName),
      );
    }

    if (this.userType) {
      var userTypeName = UserType[this.userType!];
      this.selectedFilters.push(new ChecklistItem('User type', userTypeName));
    }

    //status
    if (this.kycStatus) {
      var statusName = this.kycStatusOptions.find((x) => x.value == this.kycStatus)!.name;
      this.selectedFilters.push(new ChecklistItem('Status', statusName));
    }

    //slt
    if (this.sltStatus && !this.isSltDropdownDisabled && this.sltOptions.length > 0) {
      var sltStatusOptionName = this.sltOptions.find(
        (x) => x.id == this.sltStatus,
      )!.displayValue;
      this.selectedFilters.push(
        new ChecklistItem(this.sltStatusName, sltStatusOptionName),
      );
    }

    //search
    if (this.searchText) {
      this.selectedFilters.push(
        new ChecklistItem('Search', this.searchText, 'COMMON.SEARCH'),
      );
    }

    //date from and to
    if (this.dateFrom) {
      this.selectedFilters.push(
        new ChecklistItem(
          'Date From',
          this.dateFrom.toString(),
          'COMMON.DATE_FROM',
        ),
      );
    }
    if (this.dateTo) {
      this.selectedFilters.push(
        new ChecklistItem('Date To', this.dateTo.toString(), 'COMMON.DATE_TO'),
      );
    }

    if (this.selectedSubserviceType) {
      var subServiceType = this.subServiceTypeOptions.find(
        (s) => s.value == this.selectedSubserviceType,
      );
      this.selectedFilters.push(
        new ChecklistItem('Service Request Type', subServiceType!.displayValue),
      );
    }

    if (this.kycTag) {
      var tagName = this.kycTagOptions.find(
        (x) => x.value == this.kycTag,
      )!.name;
      this.selectedFilters.push(new ChecklistItem('Tag', tagName));
    }

    //use of premises
    if (this.useOfPremises) {
      var uopName = BuildingType[this.useOfPremises];
      this.selectedFilters.push(new ChecklistItem('Use of premises', uopName));
    }

    if (this.filterByValue != this.filterByOptions.TOTAL) {
      if (this.filterByValue == this.filterByOptions.EXISTING) {
        this.selectedFilters.push(new ChecklistItem('Type', 'Existing'));
      } else if (this.filterByValue == this.filterByOptions.NEW) {
        if (this.withAccNoSelected == 1) {
          this.selectedFilters.push(
            new ChecklistItem('Type', 'New with account number'),
          );
        } else if (this.withAccNoSelected == 2) {
          this.selectedFilters.push(
            new ChecklistItem('Type', 'New without account number'),
          );
        }
      } else if (this.filterByValue == this.filterByOptions.ONBOARD) {
        this.selectedFilters.push(
          new ChecklistItem('Type', 'Direct connection'),
        );
      } else if (this.filterByValue == this.filterByOptions.ONBOARDED) {
        if (this.kycTag) {
          var tagName = this.kycTagOptions.find((x) => x.value == this.kycTag)!.name;
          this.selectedFilters.push(new ChecklistItem('Tag', tagName));
        }
        if (this.kycStatus) {
          var statusName = this.kycStatusOptions[this.kycStatus].name;
          this.selectedFilters.push(
            new ChecklistItem('Kyc Status', statusName),
          );
        }
        this.selectedFilters.push(
          new ChecklistItem('Type', 'Onboarded Customers'),
        );
      } else if (this.filterByValue == this.filterByOptions.REGULARIZED) {
        this.selectedFilters.push(new ChecklistItem('Type', 'Regularized'));
      } else if (this.filterByValue == this.filterByOptions.RETAGGED) {
        this.selectedFilters.push(new ChecklistItem('Type', 'Retagged'));
      }
    }



    let selectedProjectDesignations = (this.projectDesignations as any[]).filter((x) => x.checked);
    for (var i = 0; i < selectedProjectDesignations.length; i++) {
      this.selectedFilters.push(
        new ChecklistItem('Project Designation', selectedProjectDesignations[i].name),
      );
          }
          let selectedProjectTypes = this.projectTypeOptions.filter((x) => x.checked);

          this.selectedProjectTypes = this.projectTypeOptions
              .filter((x) => x.checked)
              .map((x) => x.value);

          for (var i = 0; i < selectedProjectTypes.length; i++) {
              this.selectedFilters.push(
                  new ChecklistItem(
                      'Project Status',
                      selectedProjectTypes[i].name,
                      'NEW_SERVICE.PROJECT_STATUS',
                  ),
              );
          }
  }

  removeFilterStorage() {
    if (
      (this.allAreas.length > 0 && this.regions.length > 0) ||
      (this.allSubGeographicalRegions.length > 0 &&
        this.geographicalRegions.length > 0)
    )
      localStorage.removeItem('filterServiceRequestAuthorization');
  }

  selectDateType(dateType: any) {
    this.selectedDateType = dateType.name;
    this.selectedDateTypeDisplay = dateType.translation;
    this.showSelectDateType = false;
  }

  selectSearchType(searchType: any) {
    if (this.searchText && this.searchFilterApplied) {
      this.searchText = '';
      this.search();
    }
    this.selectedSearchType = searchType.name;
    this.selectedSearchTypeDisplay = searchType.translation;
    this.showSelectSearchType = false;
  }

  setFieldsVisibility() {
    let notVisibleFields: any = localStorage.getItem('notVisibleFields');

    if (!notVisibleFields) {
      return;
    }

    notVisibleFields = JSON.parse(notVisibleFields);
    this.isServiceTypeFieldVisible = notVisibleFields.find(
      (el: any) => el === 'serviceType',
    )
      ? false
      : true;
    this.isRegularizationUploadVisible = notVisibleFields.find(
      (el: any) => el === 'regularizationUpload',
    )
      ? false
      : true;
  }

  applyFilter(item: ChecklistItem) {
    this.reloadTable(1);
  }

  openDropdown(which: 'st' | 'dt' | 'sb' | 'ul', el: HTMLElement): void {
    const wasOpen = which === 'st' ? this.showSelectSearchType
                  : which === 'dt' ? this.showSelectDateType
                  : which === 'sb' ? this.showSortBy
                  : this.showUpload;
    this.showSelectSearchType = false;
    this.showSelectDateType = false;
    this.showSortBy = false;
    this.showUpload = false;
    if (!wasOpen) {
      const r = el.getBoundingClientRect();
      const zoom = parseFloat((getComputedStyle(document.documentElement) as any).zoom || '1');
      this.ddPos = { top: (r.bottom / zoom) + 'px', left: (r.left / zoom) + 'px' };
      if (which === 'st') this.showSelectSearchType = true;
      else if (which === 'dt') this.showSelectDateType = true;
      else if (which === 'sb') this.showSortBy = true;
      else this.showUpload = true;
    }
  }
}
